import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "family-law-web-design-colorado",
  metaTitle: "Family Law Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom family law web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Family Law Web Design Colorado",
  eyebrow: "Family Law Web Design — Colorado",
  h1: (
    <>
      Family Law <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded family law websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Family Law websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first family law sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Family Law web design in Colorado",
    body: "Family Law web design is the practice of building conversion-focused, mobile-first websites for family law businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with confidential intake forms." },
    { title: "Matter-type service pages", body: "Divorce, custody, support, modification, mediation, adoption — each ranks independently." },
    { title: "Confidential intake forms", body: "Conflict-check fields and secure routing — not a generic Contact Us form." },
    { title: "Attorney bio pages", body: "Each attorney with credentials, philosophy, and direct booking CTA." },
    { title: "Educational content", body: "CO-specific guides for residency, separation, and child support — earns long-tail traffic." },
    { title: "Schema and SEO at launch", body: "LegalService, Attorney, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Educational content earns the consult", body: "Family law clients research for weeks. Practice-area + how-to content builds trust before they call." },
    { heading: "Matter-type pages outrank generic divorce pages", body: "Contested divorce, mediation, and modification have different intent — each deserves its own page." },
    { heading: "Tone and trust signals close the call", body: "Reviews, philosophy, and a non-aggressive tone matter more than ad spend in family law." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a family law website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a family law website?", a: "Typical timeline is 4–6 weeks for a 12–20 page family law site with full service and service-area pages. Larger multi-location family law businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs family law businesses?", a: "No — we build family law sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Family Law Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for family law businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/family-law-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
